import { FC, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Spinner } from "../../../components/spiner/Spinner";
import Editor from "react-simple-code-editor";
import { highlight, languages } from "prismjs";
import "prismjs/components/prism-sql";
import "prismjs/components/prism-plsql";
import "prismjs/themes/prism.css";

import BaseDocumentCreator from "../../../components/documents/baseDocumentCreator";
import { PluginWrapper } from "../../../components/plugins";
import { Export1CProcedureDataProvider } from "../../../Services/DataProviders/Export1CProcedureDataProvider";
import { useAppContext } from "../../../system/providers/appContextProvider";
import { useTabsContext } from "../../../system/providers/tabsProvider";
import styles from "./styles/Export1CProcedurePluginView.module.scss";

const plsqlGrammar = languages.plsql as Prism.Grammar;

const highlightCode = (code: string): string => {
    return highlight(code, plsqlGrammar, "plsql");
};

export const Export1CProcedurePluginView: FC = () => {
    const { t } = useTranslation();
    const appContext = useAppContext();
    const tabsContext = useTabsContext();
    const dataProvider = useMemo(() => new Export1CProcedureDataProvider(appContext.coreApiService), [appContext.coreApiService]);

    const [code, setCode] = useState<string>("");
    const [initialCode, setInitialCode] = useState<string>("");
    const [loading, setLoading] = useState<boolean>(true);
    const [saving, setSaving] = useState<boolean>(false);

    useEffect(() => {
        setLoading(true);
        dataProvider.getProcedureCode((loadedCode) => {
            setCode(loadedCode);
            setInitialCode(loadedCode);
            setLoading(false);
        });
    }, [dataProvider]);

    const handleSave = () => {
        setSaving(true);
        dataProvider.updateProcedureCode(
            code,
            () => {
                setInitialCode(code);
                setSaving(false);
            },
            () => {
                setSaving(false);
            }
        );
    };

    const hasChanges = code !== initialCode;

    return (
        <PluginWrapper>
            {loading ? (
                <div className={styles.loader}>
                    <Spinner xl />
                </div>
            ) : (
                <BaseDocumentCreator
                    save={{
                        title: t("general.save"),
                        onClick: handleSave,
                        disabled: saving || !hasChanges,
                        sendRequestSpinner: saving,
                    }}
                    cancel={{
                        title: t("general.close"),
                        onClick: () => {
                            if (tabsContext.currentTab) {
                                tabsContext.close(tabsContext.currentTab);
                            }
                        },
                    }}
                >
                    <div className={styles.editorContainer}>
                        <Editor
                            value={code}
                            onValueChange={setCode}
                            highlight={highlightCode}
                            padding={16}
                            textareaClassName={styles.editorTextarea}
                            preClassName={styles.editorPre}
                            className={styles.editor}
                        />
                    </div>
                </BaseDocumentCreator>
            )}
        </PluginWrapper>
    );
};
